<template>
  <v-container id="order-summary-page">
    <v-card class="pa-10">
      <div class="_fs-36">
        {{ title }}
      </div>

      <div>{{ subtitle }}</div>

      <v-divider class="my-4"></v-divider>

      <v-row justify="center">
        <v-col
          cols="12"
          md="9">
          <p class="_fs-24">
            ข้อมูลร้านค้า หรือบริษัท
          </p>
          <template v-if="isNewLineOfficialAccountOrder">
            <div>
              ประเภทลูกค้า
              <span class="d-block d-md-inline-block primary--text font-weight-bold">
                {{ newLineOfficialAccountTypeText }}
              </span>
            </div>
            <div v-if="createOrderForm.isTransferLineOfficialAccountType">
              ช่องทางที่เคยชำระเงินมาก่อน
              <span class="d-block d-md-inline-block primary--text font-weight-bold">
                {{ transferAgencyPaymentTypeText }}
              </span>
            </div>
          </template>
          <div>
            ประเภทผู้เสียภาษี
            <span class="d-block d-md-inline-block primary--text font-weight-bold">
              {{ customerTypeText }}
            </span>
          </div>
          <div v-if="isJuristicCustomer">
            เลขที่สาขา
            <span class="d-block d-md-inline-block font-weight-bold">
              {{ createOrderForm.branchNumber }}
              <template v-if="isHeadquarter">
                (สำนักงานใหญ่)
              </template>
            </span>
          </div>
          <div>
            {{ cizenIdText }}
            <span class="d-block d-md-inline-block font-weight-bold">
              {{ createOrderForm.citizenId }}
            </span>
          </div>
          <div v-if="isJuristicCustomer">
            ชื่อร้านค้าหรือบริษัท
            <span class="d-block d-md-inline-block font-weight-bold">
              {{ createOrderForm.companyName }}
            </span>
          </div>

          <v-row no-gutters>
            <v-col cols="6">
              ชื่อ
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.firstName }}</span>
            </v-col>
            <v-col cols="6">
              นามสกุล
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.lastName }}
              </span>
            </v-col>
            <v-col cols="6">
              Email สำหรับส่งใบกำกับภาษี
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.email }}
              </span>
            </v-col>
            <v-col cols="6">
              Email สำหรับส่งใบเสนอราคา
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.emailQuotation }}
              </span>
            </v-col>
            <v-col cols="6">
              โทรศัพท์
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.tel }}
              </span>
            </v-col>
          </v-row>

          <p class="mt-4 _fs-24">
            ที่อยู่ของร้านค้า หรือบริษัท
          </p>
          <v-row no-gutters>
            <v-col cols="12">
              ที่อยู่
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.address }}
              </span>
            </v-col>
            <v-col cols="6">
              ถนน
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.street || '-' }}
              </span>
            </v-col>
            <v-col cols="6">
              แขวง/ตำบล
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.subdistrict }}
              </span>
            </v-col>
            <v-col cols="6">
              เขต/อำเภอ
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.district }}
              </span>
            </v-col>
            <v-col cols="6">
              จังหวัด
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.stateProvince }}
              </span>
            </v-col>
            <v-col cols="6">
              รหัสไปรษณีย์
              <span class="d-block d-md-inline-block font-weight-bold">
                {{ createOrderForm.zipCode }}
              </span>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <p class="mt-4 _fs-24">
            รายการที่สั่งซื้อ
          </p>
          <template v-for="item in createOrderForm.orderItems">
            <v-row
              :key="item.product"
              no-gutters>
              <v-col
                cols="6"
                md="8">
                {{ item.productName }}
                <template v-if="item.feeValue">
                  {{ decimalToPercentage(item.feeValue) }}
                </template>
                <span
                  v-if="item.premiumId"
                  class="d-block d-md-inline-block primary--text">
                  {{ item.premiumId }}
                </span>
                <span
                  v-if="item.startedAt"
                  class="d-block d-md-inline-block blue--text pl-md-1">
                  (เปิดใช้งาน {{ item.startedAt | dateTimeFormat }})
                </span>
              </v-col>
              <v-col
                cols="6"
                md="4"
                class="pl-4 text-right">
                {{ item.itemAmount | price }} บาท
              </v-col>
            </v-row>
          </template>

          <v-row no-gutters>
            <v-col cols="6">
              {{ vatText }}
            </v-col>
            <v-col
              cols="6"
              class="pl-4 text-right">
              {{ vatAmount | price }} บาท
            </v-col>
          </v-row>

          <v-row
            v-if="isJuristicCustomer && preVatAmount >= 1000"
            no-gutters>
            <v-col cols="6">
              หัก ณ ที่จ่าย
            </v-col>
            <v-col
              cols="6"
              class="pl-4 text-right">
              {{ withholding | price }} บาท
            </v-col>
          </v-row>

          <v-row
            class="mt-4"
            align="center"
            no-gutters>
            <v-col cols="6">
              ยอดที่ต้องชำระ
            </v-col>
            <v-col
              cols="6"
              class="pl-4 text-right primary--text font-weight-bold _fs-sm-24">
              {{ netTotal | price }} บาท
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>

    <div class="d-flex justify-center align-center">
      <v-checkbox
        :value="isPolicyAccepted"
        readonly
        @click="onCheckPolicy">
      </v-checkbox>
      <v-label>
        ข้าพเจ้ายอมรับ<a @click.prevent="policyActive = true">ข้อกำหนดและเงื่อนไข</a>การให้บริการนี้
      </v-label>
    </div>

    <div class="d-flex justify-center flex-row my-8">
      <v-btn
        color="primary"
        rounded
        min-width="150"
        outlined
        exact
        :to="backLinkTo">
        แก้ไขข้อมูล
      </v-btn>
      <v-btn
        class="ml-4"
        color="primary"
        rounded
        min-width="150"
        :disabled="!isPolicyAccepted"
        @click="submit">
        ยืนยัน
      </v-btn>
    </div>

    <order-privacy-policy
      :active.sync="policyActive"
      @accept="onAcceptPolicy"
      @cancel="onCancelPolicy">
    </order-privacy-policy>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { roundDecimalDigits } from '@/utils/currency'

export default {
  name: 'OrderSummaryPage',
  components: {
    OrderPrivacyPolicy: () => import('@/components/OrderPrivacyPolicy')
  },
  data () {
    return {
      policyActive: false,
      isPolicyAccepted: false,
      vat: 0.07
    }
  },
  computed: {
    ...mapState('User', ['userProfile']),
    lineUid() {
      return this.userProfile?.uid || null
    },
    ...mapState('Order', ['createOrderForm']),
    ...mapState('CustomerType', ['customerTypes']),
    ...mapGetters({
      activeBasicId: 'User/activeBasicId',
      activeLineOfficialAccount: 'User/activeLineOfficialAccount'
    }),
    orderId () {
      return this.createOrderForm.id
    },
    orderType () {
      return this.createOrderForm.type
    },
    isNewLineOfficialAccountOrder () {
      return this.orderType === 'newLineOfficialAccount'
    },
    newLineOfficialAccountTypeText () {
      if (
        this.isNewLineOfficialAccountOrder
        && this.createOrderForm.isTransferLineOfficialAccountType
      ) {
        return 'ย้ายมาจาก Agency อื่น'
      }

      if (this.isNewLineOfficialAccountOrder) {
        return 'เปิด LINE Official Account ใหม่'
      }

      return ''
    },
    transferAgencyPaymentTypeText () {
      return this.createOrderForm.transferAgencyPaymentType === 'lineThailand'
        ? 'ชำระผ่าน LINE Thailand'
        : 'ชำระผ่าน Agency เจ้าอื่น'
    },
    isEdit () {
      return this.createOrderForm.isEdit
    },
    title () {
      return this.isEdit ? 'ยืนยันการแก้ไข' : 'ยืนยันการสั่งซื้อ'
    },
    subtitle () {
      return this.isEdit
        ? 'กรุณาตรวจสอบข้อมูลก่อนทำการแก้ไขการสั่งซื้อ'
        : 'กรุณาตรวจสอบข้อมูลก่อนทำการสั่งซื้อ'
    },
    backLinkTo () {
      return this.createOrderForm.isEdit
        ? { name: 'OrderEdit', params: { productType: this.orderType, id: this.orderId } }
        : { name: 'OrderCreate', params: { productType: this.orderType || 'package' }, query: { isRenew: this.createOrderForm.isRenew } }
    },
    isJuristicCustomer () {
      return this.createOrderForm.customerTypeId !== '5'
    },
    customerTypeText () {
      const findCustomerType = this.customerTypes.find(
        (customerType) => this.createOrderForm.customerTypeId === customerType.value
      )

      return findCustomerType ? findCustomerType.text : '-'
    },
    cizenIdText () {
      return this.isJuristicCustomer
        ? 'เลขประจำตัวผู้เสียภาษี'
        : 'เลขประจำตัวประชาชน'
    },
    isHeadquarter () {
      return this.createOrderForm.branchNumber === '00000'
    },
    isTransferLineOfficialAccountType () {
      return this.createOrderForm.isTransferLineOfficialAccountType
    },
    basicId () {
      if (this.isTransferLineOfficialAccountType) {
        return this.createOrderForm.basicId
      }

      if (this.isNewLineOfficialAccountOrder) {
        return undefined
      }

      return this.activeBasicId
    },
    preVatAmount () {
      return this.createOrderForm.orderItems
        ? roundDecimalDigits(
          this.createOrderForm.orderItems.reduce((acc, cur) => acc + cur.itemAmount, 0)
        )
        : 0
    },
    vatAmount () {
      return roundDecimalDigits(this.preVatAmount * this.vat)
    },
    withholding () {
      return roundDecimalDigits(this.preVatAmountWithholding)
    },
    vatText () {
      return `vat ${this.decimalToPercentage(this.vat)}`
    },
    grandTotal () {
      return roundDecimalDigits(this.preVatAmount + this.vatAmount)
    },
    netTotal () {
      // eslint-disable-next-line max-len
      return this.isJuristicCustomer && this.preVatAmount >= 1000 ? roundDecimalDigits(this.grandTotal - this.withholding) : roundDecimalDigits(this.grandTotal)
    },
    withholdingTax () {
      const findCustomerType = this.customerTypes.find(
        (customerType) => this.createOrderForm.customerTypeId === customerType.value
      )
      return roundDecimalDigits(findCustomerType.percentWithholding / 100.0)
    },
    preVatAmountWithholding () {
      return roundDecimalDigits(this.preVatAmount * this.withholdingTax)
    },
    preVatAmountVatAmount () {
      return roundDecimalDigits(this.preVatAmount + this.vatAmount)
    }
  },
  beforeRouteEnter (to, from, next) {
    if (from.name === 'OrderCreate' || from.name === 'OrderEdit') {
      next()
    }
  },
  methods: {
    ...mapActions({
      createOrder: 'Order/createOrder',
      setCreateOrderForm: 'Order/setCreateOrderForm',
      updateOrderById: 'Order/updateOrderById'
    }),
    ...mapWaitingActions('Order', {
      createOrder: 'app loading',
      updateOrderById: 'app loading'
    }),
    decimalToPercentage (value) {
      return `${parseInt(value * 100, 10)}%`
    },
    toGa4Items (orderItems) {
      if (!orderItems || !orderItems.length) return []
      return orderItems.map((item) => ({
        item_id: item.productCode || item.product,
        item_name: item.productName,
        item_category: item.productType,
        price: item.unitPrice ?? 0,
        quantity: item.itemQty ?? 0
      }))
    },
    onCheckPolicy () {
      this.policyActive = true
    },
    onAcceptPolicy () {
      this.policyActive = false
      this.isPolicyAccepted = true
    },
    onCancelPolicy () {
      this.policyActive = false
      this.isPolicyAccepted = false
    },
    async submit () {
      const withholdingTaxAmount = roundDecimalDigits(this.preVatAmount * this.withholdingTax)

      const type = this.isNewLineOfficialAccountOrder
        && this.createOrderForm.isTransferLineOfficialAccountType
        ? 'transferLineOfficialAccount'
        : this.orderType

      const transferAgencyPaymentType = this.createOrderForm.isTransferLineOfficialAccountType
        ? this.createOrderForm.transferAgencyPaymentType
        : undefined

      const params = {
        ...this.createOrderForm,
        vat: this.vat,
        preVatAmount: this.preVatAmount,
        vatAmount: this.vatAmount,
        grandTotal: this.grandTotal,
        withholdingTax: this.withholdingTax,
        withholdingTaxAmount,
        netTotal: this.netTotal,
        basicId: this.basicId,
        type,
        transferAgencyPaymentType
      }

      if (this.isEdit) {
        await this.updateOrderById({ id: this.orderId, params })
      } else {
        window.dataLayer = window.dataLayer || []

        if (this.createOrderForm.orderItems) {
          let label = ''
          let event = ''
          this.createOrderForm.orderItems.forEach((item) => {
            switch (item.productType) {
              case 'package':
                label = 'existing_btn_package_broadcast_add_oa_confirm'
                event = 'click_btn_package_broadcast_add_oa_confirm'
                break
              case 'message':
                label = 'existing_btn_package_broadcast_add_msg_confirm'
                event = 'click_btn_package_broadcast_add_msg_confirm'
                break
              case 'premiumId':
                label = 'existing_btn_package_broadcast_add_premium_confirm'
                event = 'click_btn_package_broadcast_add_premium_confirm'
                break
              default:
                label = null
            }
            window.dataLayer.push({
              event,
              label,
              value: 1,
              userId: this.lineUid,
              premium_id: this.activeLineOfficialAccount.premiumId || null,
              basic_id: this.activeLineOfficialAccount.basicId || null,
              customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new',
              customer_type: this.activeLineOfficialAccount.customerTypeId === '5' ? 'บุคคลธรรมดา' : 'นิติบุคคล',
              orderItems: JSON.stringify(this.toGa4Items(params.orderItems)),
              pre_vat_amount: params.preVatAmount || null,
              vat: params.vat || null,
              vat_amount: params.vatAmount || null,
              witholding_tax_amount: params.withholdingTaxAmount ?? null
            })
          })
        }

        if (this.isNewLineOfficialAccountOrder) {
          window.dataLayer.push({
            event: 'click_submit_btn_confirm_by_new',
            category: 'user_action',
            label: 'new_btn_package_broadcast_confirm',
            value: 1,
            userId: this.lineUid,
            premium_id: this.activeLineOfficialAccount.premiumId || null,
            basic_id: this.activeLineOfficialAccount.basicId || null,
            customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new',
            customer_type: this.activeLineOfficialAccount.customerTypeId === '5' ? 'บุคคลธรรมดา' : 'นิติบุคคล',
            orderItems: JSON.stringify(this.toGa4Items(params.orderItems)),
            pre_vat_amount: params.preVatAmount || null,
            vat: params.vat || null,
            vat_amount: params.vatAmount || null,
            witholding_tax_amount: params.withholdingTaxAmount ?? null
          })
        } else {
          let label = ''
          let event = ''
          switch (this.orderType) {
            case 'package':
              label = 'existing_btn_package_broadcast_confirm'
              event = 'click_btn_package_broadcast_confirm'
              break
            case 'message':
              label = 'existing_btn_msg_comfirm'
              event = 'click_btn_msg_confirm'
              break
            case 'premiumId':
              label = 'existing_btn_premium_comfirm'
              event = 'click_btn_premium_confirm'
              break
            case 'oaChat':
              label = 'existing_btn_oa_comfirm'
              event = 'click_btn_oa_confirm'
              break
            default:
              label = null
          }

          if (label) {
            window.dataLayer.push({
              event,
              category: 'user_action',
              label,
              value: 1,
              userId: this.lineUid,
              premium_id: this.activeLineOfficialAccount.premiumId || null,
              basic_id: this.activeLineOfficialAccount.basicId || null,
              customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new',
              customer_type: this.activeLineOfficialAccount.customerTypeId === '5' ? 'บุคคลธรรมดา' : 'นิติบุคคล',
              orderItems: JSON.stringify(this.toGa4Items(params.orderItems)),
              pre_vat_amount: params.preVatAmount || null,
              vat: params.vat || null,
              vat_amount: params.vatAmount || null,
              witholding_tax_amount: params.withholdingTaxAmount ?? null
            })
          }
        }
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'confirm_order_summary',
          category: 'user_action',
          label: 'btn_confirm_order_summary',
          value: 1,
          userId: this.lineUid,
          premium_id: this.activeLineOfficialAccount.premiumId || null,
          basic_id: this.activeLineOfficialAccount.basicId || null,
          customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new',
          customer_type: this.activeLineOfficialAccount.customerTypeId === '5' ? 'บุคคลธรรมดา' : 'นิติบุคคล',
          orderItems: JSON.stringify(this.toGa4Items(params.orderItems)),
          pre_vat_amount: params.preVatAmount || null,
          vat: params.vat || null,
          vat_amount: params.vatAmount || null,
          witholding_tax_amount: params.withholdingTaxAmount ?? null
        })
        await this.createOrder(params)
      }

      const formEdited = this.isEdit

      this.setCreateOrderForm({})
      this.$router.push({ name: 'OrderSuccess', query: { isEdit: formEdited } })
    }
  }
}
</script>
